import React, { useMemo, useState, useRef, useEffect, useCallback } from "react"
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6"
import { MdTimer, MdContentCopy } from "react-icons/md"
import { Calendar, momentLocalizer } from "react-big-calendar"
import moment from "moment-timezone"
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop"
import "react-big-calendar/lib/css/react-big-calendar.css"
import "react-big-calendar/lib/addons/dragAndDrop/styles.css"
import Swal from "sweetalert2"
import WorkoutModal from "pages/Workouts/addWorkout"
import { FetchPutData } from "utils/Common_api_functions"
import { Table } from "reactstrap"
import "moment/locale/en-gb"

moment.tz.setDefault("UTC")
moment.updateLocale("en-gb", { week: { dow: 1 } })

const DnDCalendar = withDragAndDrop(Calendar)
const localizer = momentLocalizer(moment)

const TrainingPlanCalendar = ({
  selectedPlan,
  getDraggedEvent,
  handleSelectEvent,
  activities,
  handleDropFromOutside,
  onEventResize,
  handleDeleteEvent,
  setselectedActivity,
  onEventDrop,
  setLoading,
  setEvents,
  setSelectedPlan,
  setTrainingPlans,
}) => {
  const virtualStartDate = useMemo(() => {
    const startOfMonth = moment().startOf("month")
    const firstMonday = startOfMonth.startOf("isoWeek")
    return firstMonday.toDate()
  }, [])

  const firstDayOfCurrentMonth = useMemo(
    () => moment().startOf("month").toDate(),
    []
  )

  const [currentStartDate, setCurrentStartDate] = useState(
    firstDayOfCurrentMonth
  )
  const [copiedEvent, setCopiedEvent] = useState(null) // ← clipboard

  // Clear clipboard on Escape
  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") setCopiedEvent(null)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  const duration = Number(selectedPlan?.duration) || 1
  const type = selectedPlan?.type || "day"

  const totalDays = useMemo(() => {
    if (type === "week") return duration * 7
    if (type === "month") return duration * 30
    return duration
  }, [duration, type])

  const planPeriods = useMemo(() => {
    return [...Array(totalDays)].map((_, i) => {
      const date = moment(virtualStartDate).add(i, "days").toDate()
      const label =
        type === "week"
          ? `Week ${Math.floor(i / 7) + 1}`
          : type === "month"
          ? `Month ${Math.floor(i / 30) + 1}`
          : `Day ${i + 1}`
      return { date, dayIndex: i + 1, label }
    })
  }, [virtualStartDate, totalDays, type])

  const isDateInPlan = date => {
    const planStart = moment.utc(virtualStartDate).startOf("day")
    const planEnd = moment
      .utc(virtualStartDate)
      .add(totalDays - 1, "days")
      .endOf("day")
    const mDate = moment.utc(date)
    return (
      mDate.isSameOrAfter(planStart, "day") &&
      mDate.isSameOrBefore(planEnd, "day")
    )
  }

  const dayPropGetter = date => {
    if (isDateInPlan(date)) {
      return {
        className: "plan-day",
        style: {
          backgroundColor: copiedEvent ? "#f0f9ff" : "#fff", // subtle tint in paste mode
          cursor: copiedEvent ? "copy" : "pointer",
        },
      }
    }
    return {
      className: "blocked-day",
      style: {
        backgroundColor: "#dadada",
        cursor: "not-allowed",
        pointerEvents: "none",
        color: "#ccc",
      },
    }
  }

  const normalizedEvents = useMemo(() => {
    if (!selectedPlan?.events || !planPeriods.length) return []
    return selectedPlan.events
      .map(ev => {
        if (!ev.dayNumber || ev.dayNumber.length === 0) return null
        const dayNumbers = (
          Array.isArray(ev.dayNumber) ? ev.dayNumber : [ev.dayNumber]
        ).map(n => Math.abs(Number(n)))
        const startPeriod = planPeriods.find(
          p => p.dayIndex === Math.min(...dayNumbers)
        )
        const endPeriod = planPeriods.find(
          p => p.dayIndex === Math.max(...dayNumbers)
        )
        if (!startPeriod || !endPeriod) return null
        let start = moment(startPeriod.date)
        let end = moment(endPeriod.date)
        if (ev.start) {
          const s = moment(ev.start)
          start = start.set({
            hour: s.hour(),
            minute: s.minute(),
            second: s.second(),
          })
        }
        if (ev.end) {
          const e = moment(ev.end)
          end = end.set({
            hour: e.hour(),
            minute: e.minute(),
            second: e.second(),
          })
        }
        if (start.startOf("day").isBefore(end.startOf("day"))) {
          end = end.add(1, "day")
        }
        return {
          ...ev,
          start: start.toDate(),
          end: end.toDate(),
          allDay: false,
          title: ev.title || ev.workout?.activityTitle || "Untitled Workout",
        }
      })
      .filter(Boolean)
  }, [selectedPlan, planPeriods])

  const maxPeriods = Math.ceil(totalDays / 30)
  const currentPeriodIndex = Math.floor(
    moment(currentStartDate).diff(moment(firstDayOfCurrentMonth), "days") / 30
  )
  const isNextDisabled = currentPeriodIndex >= maxPeriods - 1
  const isPrevDisabled = moment(currentStartDate).isSameOrBefore(
    moment(firstDayOfCurrentMonth),
    "month"
  )

  const handleNavigate = action => {
    if (action === "PREV" && !isPrevDisabled) {
      const newDate = moment(currentStartDate).subtract(1, "month")
      if (newDate.isBefore(moment(virtualStartDate), "month")) return
      setCurrentStartDate(newDate.toDate())
    } else if (action === "NEXT" && !isNextDisabled) {
      setCurrentStartDate(moment(currentStartDate).add(1, "month").toDate())
    }
  }

  const getCurrentPeriodLabel = () =>
    `${
      type === "day" ? "Days" : type === "week" ? "Weeks" : "Months"
    } Training Plan (${duration} ${type}s)`

  const [userworkoutmodal, setUserWorkoutModal] = useState(false)
  const [startDate, setStartDate] = useState(null)
  const [selectedDayNumber, setSelectedDayNumber] = useState(null)
  const draggedWorkoutRef = useRef(null)

  // Shared insert logic — accepts an explicit dayNumber so paste can supply its own
  const handleCreateWorkoutforuser = useCallback(
    async (workoutData, targetDayNumber, targetDate) => {
      const resolvedDayNumber = targetDayNumber ?? selectedDayNumber
      const resolvedDate = targetDate ?? startDate

      const newEvent = {
        title: workoutData.activityTitle || "Untitled Workout",
        start: resolvedDate,
        end: resolvedDate,
        allDay: true,
        workout: workoutData,
        dayNumber: [resolvedDayNumber],
      }

      setEvents(prev => [...prev, newEvent])

      try {
        const APIdata = await FetchPutData(
          setLoading,
          "training/plan/insert/workout",
          selectedPlan._id,
          { event: newEvent }
        )

        if (!APIdata?.data?.success) throw new Error(APIdata?.res?.message)

        const updatedEvents =
          APIdata?.data?.events || APIdata?.data?.workoutCalender || []
        const normalized = updatedEvents.map(ev => ({
          ...ev,
          title: ev.title || ev.workout?.activityTitle || "Untitled Workout",
          start: new Date(ev.start),
          end: new Date(ev.end),
        }))

        setSelectedPlan(prev => ({ ...prev, events: normalized }))
        setTrainingPlans(prev =>
          prev.map(p =>
            p._id === selectedPlan._id ? { ...p, events: normalized } : p
          )
        )
      } catch (err) {
        Swal.fire({
          title: "Error",
          icon: "error",
          text: err.message || "Failed to add workout",
        })
        setEvents(prev => prev.filter(ev => ev !== newEvent))
      }

      draggedWorkoutRef.current = null
    },
    [
      selectedDayNumber,
      startDate,
      selectedPlan,
      setEvents,
      setSelectedPlan,
      setTrainingPlans,
      setLoading,
    ]
  )

  const handleSelectSlot = slotInfo => {
    if (!isDateInPlan(slotInfo.start)) {
      Swal.fire({
        icon: "warning",
        title: "Not Allowed",
        text: "You cannot add events outside your plan range!",
      })
      return
    }

    const selectedPeriod = planPeriods.find(p =>
      moment(p.date).isSame(slotInfo.start, "day")
    )

    if (copiedEvent) {
      // Paste mode: insert copied workout on the clicked day
      handleCreateWorkoutforuser(
        copiedEvent.workout,
        selectedPeriod?.dayIndex,
        slotInfo.start
      )
      // Uncomment below to clear after a single paste:
      // setCopiedEvent(null)
    } else {
      setSelectedDayNumber(selectedPeriod?.dayIndex)
      setStartDate(slotInfo.start)
      setselectedActivity(null)
      setUserWorkoutModal(true)
    }
  }

  const handleDropFromOutsideValidated = args => {
    if (!isDateInPlan(args.start)) {
      Swal.fire({
        icon: "warning",
        title: "Not Allowed",
        text: "You cannot drop events outside your plan!",
      })
      return
    }
    const droppedDate = moment(args.start).startOf("day")
    const matchedPeriod = planPeriods.find(p =>
      moment(p.date).isSame(droppedDate, "day")
    )
    handleDropFromOutside?.({
      ...args,
      start: args.start,
      end: args.end || args.start,
      dayNumber: matchedPeriod?.dayIndex,
    })
  }

  const onEventDropValidated = args => {
    const { event, start, end } = args
    if (!isDateInPlan(start)) {
      Swal.fire({
        icon: "warning",
        title: "Not Allowed",
        text: "Cannot move outside plan!",
      })
      return
    }
    const planEnd = moment(virtualStartDate)
      .add(totalDays - 1, "days")
      .endOf("day")
    const safeEnd = moment(end).isAfter(planEnd) ? planEnd.toDate() : end
    const newDayNumber =
      moment(start).diff(moment(virtualStartDate), "days") + 1
    const safeDayNumber = Math.max(1, Math.min(totalDays, newDayNumber))
    onEventDrop?.({
      ...args,
      event: { ...event, start, end: safeEnd, dayNumber: [safeDayNumber] },
    })
  }

  const onEventResizeValidated = args => {
    if (!isDateInPlan(args.start) || !isDateInPlan(args.end)) {
      Swal.fire({
        icon: "warning",
        title: "Not Allowed",
        text: "Cannot resize outside plan!",
      })
      return
    }
    onEventResize?.(args)
  }

  const CustomDateHeader = ({ label, date }) => {
    const period = planPeriods.find(p => moment(p.date).isSame(date, "day"))
    return (
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: period ? "#222" : "#aaa",
        }}
      >
        {period ? `Day ${period.dayIndex}` : ""}
      </div>
    )
  }

  const CustomToolbar = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 10,
        borderBottom: "2px solid #e0e0e0",
        backgroundColor: "#fafafa",
      }}
    >
      <div className="d-flex gap-2">
        <button
          onClick={() => handleNavigate("PREV")}
          disabled={isPrevDisabled}
          style={{
            padding: "5px 10px",
            cursor: isPrevDisabled ? "not-allowed" : "pointer",
            border: "1px solid #ddd",
            borderRadius: 4,
            backgroundColor: "#fff",
            opacity: isPrevDisabled ? 0.5 : 1,
          }}
        >
          <FaChevronLeft />
        </button>
        <button
          onClick={() => handleNavigate("NEXT")}
          disabled={isNextDisabled}
          style={{
            padding: "5px 10px",
            cursor: isNextDisabled ? "not-allowed" : "pointer",
            border: "1px solid #ddd",
            borderRadius: 4,
            backgroundColor: "#fff",
            opacity: isNextDisabled ? 0.5 : 1,
          }}
        >
          <FaChevronRight />
        </button>
      </div>
      <div style={{ fontWeight: "bold", fontSize: 14, color: "#666" }}>
        {getCurrentPeriodLabel()}
      </div>
      <div style={{ fontWeight: "bold", fontSize: 14, color: "#333" }}>
        {selectedPlan?.planName} Training Plan
      </div>
    </div>
  )

  const CustomEvent = ({ event }) => {
    const handleCopy = e => {
      e.stopPropagation()
      setCopiedEvent(event)
    }

    return (
      <div
        style={{
          padding: 2,
          margin: "0 2px",
          position: "relative",
          outline:
            copiedEvent && copiedEvent === event ? "2px solid #4ade80" : "none",
          borderRadius: 4,
        }}
      >
        <div className="fw-bold mb-1" style={{ fontSize: 11 }}>
          {event?.title}
        </div>
        <div style={{ fontSize: 11 }}>
          <MdTimer size={14} /> {event?.workout?.plannedDuration}
        </div>
        <Table className="mb-0 mt-2 text-white gap-2">
          {event?.workout?.buildWorkout?.map((item, index) => (
            <tr key={index}>
              <td style={{ fontSize: "12px" }} className="pe-2">
                {item?.typeOption
                  ? item.typeOption.charAt(0).toUpperCase()
                  : ""}
              </td>
              <td style={{ fontSize: "12px" }} className="pe-2">
                {item?.duration}
              </td>
              <td style={{ fontSize: "12px" }}>
                {item?.zone ? item.zone.replace(/zone/gi, "Z") : ""}
              </td>
            </tr>
          ))}
        </Table>

        {/* Copy button */}
        <button
          onClick={handleCopy}
          title="Copy workout"
          style={{
            position: "absolute",
            top: 2,
            right: 20,
            background: "#fff",
            border: "none",
            color: "#3b82f6",
            cursor: "pointer",
            padding: 2,
          }}
        >
          <MdContentCopy size={12} />
        </button>

        {/* Delete button */}
        <button
          onClick={e => {
            e.stopPropagation()
            handleDeleteEvent(event)
          }}
          style={{
            position: "absolute",
            top: 2,
            right: 2,
            background: "#fff",
            border: "none",
            color: "red",
            cursor: "pointer",
            padding: 2,
          }}
        >
          ✕
        </button>
      </div>
    )
  }

  return (
    <div style={{ padding: 20, height: "100%" }}>
      {/* Paste-mode banner */}
      {copiedEvent && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#1e40af",
            color: "#fff",
            borderRadius: 6,
            padding: "6px 14px",
            marginBottom: 8,
            fontSize: 13,
          }}
        >
          <span>
            📋 <strong>{copiedEvent.title || "Untitled Workout"}</strong> copied
            — click any plan day to paste. Press{" "}
            <kbd
              style={{
                background: "rgba(255,255,255,0.2)",
                borderRadius: 3,
                padding: "0 4px",
              }}
            >
              Esc
            </kbd>{" "}
            to cancel.
          </span>
          <button
            onClick={() => setCopiedEvent(null)}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
      )}

      <DnDCalendar
        localizer={localizer}
        events={normalizedEvents}
        views={["month"]}
        date={currentStartDate}
        onNavigate={() => {}}
        onEventDrop={onEventDropValidated}
        onEventResize={onEventResizeValidated}
        resizable
        selectable
        popup
        dragFromOutsideItem={getDraggedEvent}
        onDropFromOutside={handleDropFromOutsideValidated}
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlot}
        dayPropGetter={dayPropGetter}
        components={{
          toolbar: CustomToolbar,
          event: CustomEvent,
          month: { dateHeader: CustomDateHeader },
        }}
        style={{ height: "100%" }}
      />

      <WorkoutModal
        isOpen={userworkoutmodal}
        toggle={() => setUserWorkoutModal(false)}
        onSave={workoutData => handleCreateWorkoutforuser(workoutData)}
        startDate={startDate}
      />
    </div>
  )
}

export default TrainingPlanCalendar
